/**
 * Block: Front page hero
 *
 * Scroll-scrubbed hero. As the user scrolls, a pre-decoded image sequence
 * (extracted offline from the descent footage) is painted to a <canvas>.
 * ScrollTrigger only pins the section and reports raw scroll progress; the
 * frame index is driven by an eased playhead (see _tick) that coasts toward the
 * scroll position and settles in the direction you scrolled after you stop — a
 * monotonic ease, so it never springs back past the target. The intro and CMS
 * text groups live in a DOM overlay and are revealed from the same eased value.
 *
 * We deliberately never scrub <video>.currentTime — iOS Safari re-decodes from
 * the nearest keyframe on every seek and stutters. Pre-decoded ImageBitmap
 * blits stay smooth everywhere.
 *
 * Perceived smoothness here is mostly a question of how many frames a given amount of
 * scrolling gets to show, so the numbers in bin/build-hero-frames.sh (frame count,
 * pre-cropped aspect) and data-scroll-distance in the .twig are as much a part of this
 * animation as the easing is. Rule of thumb: (scroll-distance × viewport height) ÷
 * frames = pixels per frame; at a reading-pace 200px/s scroll that yields 200 ÷ that
 * many frames per second, and it wants to stay in the twenties.
 *
 * Frames are static theme assets under /static/hero/, rebuilt with
 * bin/build-hero-frames.sh. The markup is rendered server-side; in the block
 * editor (is_preview) and under prefers-reduced-motion the static stacked
 * layout is shown instead — no pin, no scrub (see the .twig + .is-static SCSS).
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// iOS/Android fire a window 'resize' every time the address bar shows/hides. Let GSAP
// skip the internal refresh those cause; our own _resize applies the same guard. Without
// this the pinned hero re-pins mid-scroll and the rest of the page fights the user's
// scroll on touch.
ScrollTrigger.config({ ignoreMobileResize: true });

const reducedMotion = () =>
  !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

// How fast a text group's entry tracks the finger, in px of travel per px of scroll. This is
// what times the reveals (see _computeGroupWindows) — a duration would not, because the
// distance a group covers depends on where the SCSS parks it, and one shared duration made
// them arrive at wildly different speeds. Above ~2 the copy outruns the gesture and reads as
// detached from it; at 1 it moves exactly with the finger, which for something sweeping in
// from off-stage is sluggish. Exits are untimed by this and stay on the shared fade band.
const ENTER_TRACK = 1.6;

// Hermite smoothstep: eased 0→1 ramp between edges a and b.
const smoothstep = (a, b, x) => {
  if (a === b) return x < a ? 0 : 1;
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

class FrontPageHero {
  constructor(section) {
    this.section = section;
    this.stage = section.querySelector('.front-page-hero__stage');
    this.canvas = section.querySelector('[data-fph-canvas]');
    this.poster = section.querySelector('.front-page-hero__poster');
    this.intro = section.querySelector('[data-fph-intro]');
    this.groups = Array.from(section.querySelectorAll('[data-fph-group]'));

    this.ext = section.dataset.frameExt || 'webp';

    // Two frame sets, each pre-cropped to the aspect it will be drawn at: the desktop
    // set is 16:9 (250×1920×1080), the mobile set square (125×720²). Pre-cropping is
    // what pays for the frame density — a square desktop frame threw away 44% of every
    // decoded pixel, so the same memory now buys 2.5× the frames. The desktop set still
    // decodes to ~2 GB of ImageBitmaps held at once, which kills the tab on iOS, so
    // touch devices (incl. iPad in landscape, which is ≥1200px) and low-memory devices
    // get the ~260 MB set + shorter pin + snappier damping + lower DPR; see _pickSet.
    this.mqlDesktop = window.matchMedia ? window.matchMedia('(min-width: 1200px)') : { matches: true };
    this.baseDesktop = section.dataset.framesBase || '';
    this.baseMobile = section.dataset.framesBaseMobile || this.baseDesktop;
    this.countDesktop = parseInt(section.dataset.frameCount, 10) || 0;
    this.countMobile = parseInt(section.dataset.frameCountMobile, 10) || this.countDesktop;
    this.scrollDistance = parseInt(section.dataset.scrollDistance, 10) || 300;
    this._pickSet(); // sets base, count, screens, tau, dpr, useMobileSet for this device

    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
    this.frames = [];
    this.frameState = { i: 0 };
    this._lastDrawnIdx = -1; // _tick re-blits only when the rounded frame index changes
    this.bgColor = { r: 2, g: 2, b: 38 };

    // Cover-crop aspect: 16:9 for the desktop set, 1:1 for the mobile one. This
    // follows the LOADED SET, not the viewport — the frames are pre-cropped to their
    // aspect, so re-cropping them to the other one would just over-zoom. It matters
    // for the one case where the two disagree: a desktop browser (16:9 set, frozen at
    // init) resized below the 1200px breakpoint. Constant for the session, since
    // _pickSet freezes the set — hence no recompute in _resize.
    this.targetAspect = this.useMobileSet ? 1 : 16 / 9;

    // Crop focal point (object-position semantics, 0..1): the falling node sits on
    // the RIGHT of the footage, so the tight 1:1 mobile crop needs to bias right to
    // keep it. focusX = p places source point p at p of the viewport, so it's always
    // visible on any aspect. focusX drives the horizontal window; focusY the vertical
    // source crop — now a no-op for both sets, since each frame already arrives at its
    // target aspect (the desktop set's vertical crop is baked in by
    // bin/build-hero-frames.sh, CROP_Y). Kept because it still applies to any set whose
    // aspect differs from the draw target. Author via data-focus-x/y (0-100).
    const fx = parseFloat(section.dataset.focusX);
    const fy = parseFloat(section.dataset.focusY);
    this.focusX = Number.isFinite(fx) ? clamp01(fx / 100) : 0.5;
    this.focusY = Number.isFinite(fy) ? clamp01(fy / 100) : 0.5;

    // How much of the scrub happens BEFORE the section pins, as a percentage of the whole
    // (data-lead-in). The descent starts moving while it is still sliding up into view, so
    // the hand-off from the static surface band above reads as one continuous dive rather
    // than a cut to a still frame that only comes alive once it locks. Carved out of the
    // pinned length rather than added to it, so frame density is unaffected — see
    // _setupScroll. _leadFraction() caps it at the one viewport the geometry allows.
    const lead = parseFloat(section.dataset.leadIn);
    this.leadFrac = Number.isFinite(lead) ? Math.min(Math.max(lead / 100, 0), 0.9) : 0.3;

    // How far the LAST group keeps rising after it has arrived, as a percentage of the stage
    // height (data-drift). The last group has no exit — it holds to the end of the scrub —
    // so from the moment its entry settles there is nothing on screen accountable to the
    // scroll except the footage, and across the quieter frames that reads as the page having
    // stopped responding. This gives that stretch something that answers the finger. Kept
    // slow on purpose: it is feedback, not a second entrance. See _computeGroupWindows.
    const drift = parseFloat(section.dataset.drift);
    this.driftFrac = Number.isFinite(drift) ? Math.max(drift / 100, 0) : 0.08;

    // How far below its resting place the FIRST group starts, as a percentage of the stage
    // height (data-rise). It is already on screen when the descent slides into view, so it
    // cannot sweep up from below the frame the way the later groups do; instead it sits a
    // little low and rises into place, arriving where every other group does. That makes the
    // copy land as a move of its own rather than being parked and waiting for the section to
    // arrive underneath it, and it is what keeps the long stretch either side of the pin lock
    // answering the scroll. It never leaves the frame, so this is a settle, not an entrance —
    // but it is also the only thing moving for the first half of the scrub, so it wants to be
    // clearly quicker than the drift at the other end. See _computeGroupWindows.
    const rise = parseFloat(section.dataset.rise);
    this.riseFrac = Number.isFinite(rise) ? Math.max(rise / 100, 0) : 0.16;

    // Playhead smoothing. Scroll sets `targetP` (0..1); each frame `currentP`
    // eases toward it with an exponential lerp. It's monotonic (never crosses
    // the target), so the video keeps coasting toward where you scrolled and
    // decelerates to rest — always settling in the scrolled direction, with no
    // spring-back. `tau` = smoothing time constant in seconds (higher = more
    // coast/lag); tunable via data-smooth.
    //
    // Three knobs shape the feel, all authorable and all measured in units you can
    // reason about. Worth knowing what damping can and cannot do here: it never creates
    // frames, it only redistributes WHEN the frames a scroll gesture earned get shown.
    // During sustained scrolling the playhead already paints a new frame every display
    // refresh, so there is nothing left to win there — the whole perceptible difference
    // is in the tail of a gesture and in how far behind the playhead runs.
    //
    //   tau       (data-smooth, seconds) — smoothing time constant. Steady-state lag is
    //             velocity x tau, so this is literally "how many milliseconds behind the
    //             scroll the footage runs". Higher = more float/weight, more disconnect.
    //   settleFps (data-settle, frames/sec) — floor on the playhead's speed. The
    //             exponential approach is asymptotic: it closes a fixed FRACTION of the
    //             remaining gap per second, so without a floor the last stretch crawls.
    //             That was half the original "choppy" report — after a scroll stopped the
    //             final frames landed 217ms and then 467ms apart, a clunk exactly where
    //             the motion should have been settling. The floor also makes the playhead
    //             land EXACTLY on target, which is what lets _tick stop on an equality
    //             test instead of an epsilon. Raising it shortens the coast and makes it
    //             read as motion rather than drift; lowering it lengthens the coast at the
    //             cost of visible steps.
    //   maxSpeed  (data-max-speed, frames/sec) — ceiling on how fast the playhead may
    //             advance, so a hard flick PLAYS the descent quickly instead of skipping
    //             through it. 60 is the point where nothing is ever skipped (one new frame
    //             per display refresh) but it's also near enough to a no-op on a 60Hz
    //             display: the value only bites below the refresh rate, which is why the
    //             default sits at 40. 0 disables it.
    //   maxLag    (data-max-lag, frames) — cap on how far behind the playhead may fall.
    //             Because lag is velocity x tau it is otherwise unbounded, and a scrollbar
    //             drag or End key would make the footage grind through the whole descent
    //             long after the scroll finished.
    //
    // maxSpeed and maxLag are two ends of the same constraint, and they FIGHT. A speed
    // ceiling only bites while the playhead is ALLOWED to fall behind: once lag saturates,
    // the maxLag clamp tracks the scroll directly, which is fast-forwarding — exactly what
    // the ceiling was meant to prevent. Measured at 900px/s (a 102fps demand): maxLag 20
    // played at 98fps (ceiling defeated), maxLag 60 played at 40.0fps (ceiling governing).
    //
    // So the pair encodes a CHOICE, and the choice here is continuous playback over quick
    // settling. You cannot have both: the frames a gesture earns are fixed by the distance
    // scrolled, so refusing to skip them means showing them later.
    //
    //     catch-up seconds = maxLag / maxSpeed        (60 / 40 = 1.5s worst case)
    //
    // maxLag is now purely a safety valve — big enough to stay out of the way of normal
    // gestures, small enough that a scrollbar drag or End key can't leave the footage
    // grinding on for many seconds. Past it the system degrades gracefully back to
    // skipping frames (the clamp tracks scroll continuously rather than jumping once).
    const settle = parseFloat(section.dataset.settle);
    this.settleFps = Number.isFinite(settle) ? settle : 30;
    const maxLagFrames = parseFloat(section.dataset.maxLag);
    this.maxLagFrames = Number.isFinite(maxLagFrames) ? maxLagFrames : 60;
    const maxSpeed = parseFloat(section.dataset.maxSpeed);
    this.maxSpeedFps = Number.isFinite(maxSpeed) ? maxSpeed : 40;
    this._deriveFeel();

    this.targetP = 0;
    this.currentP = 0;
    this.raf = null;
    this.lastTime = 0;
    this.fade = 0.12;
    this.groupWindows = [];
    this._lastW = window.innerWidth;
    this._lastH = window.innerHeight;

    this._resize = this._resize.bind(this);
    this._render = this._render.bind(this);
    this._tick = this._tick.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
  }

  // Choose the frame set + motion budget for this device. Any touchscreen (phone,
  // iPad — including landscape, which is ≥1200px — Android, touch laptop) or a
  // low-memory device gets the lighter mobile set so it never tries to hold the
  // desktop set's ~2 GB of decoded frames. Also shortens the pin, snappier damping
  // and a lower DPR cap so the pinned descent doesn't monopolise/jank touch scrolling.
  // Called once at construction; the chosen set is frozen for the session (no runtime
  // swap — see _resize), while dpr/targetAspect still adapt on resize.
  _pickSet() {
    const isTouch = (navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;
    const lowMem = !!(navigator.deviceMemory && navigator.deviceMemory <= 4);
    this.useMobileSet = !this.mqlDesktop.matches || isTouch || lowMem;
    const m = this.useMobileSet;
    this.base = m ? this.baseMobile : this.baseDesktop;
    this.count = m ? this.countMobile : this.countDesktop;
    this.screens = m ? Math.min(this.scrollDistance / 100, 1.75) : this.scrollDistance / 100;
    const tau = Number.isFinite(parseFloat(this.section.dataset.smooth)) ? parseFloat(this.section.dataset.smooth) : 0.32;
    this.tau = m ? Math.min(tau, 0.12) : tau;
    this.dpr = Math.min(window.devicePixelRatio || 1, m ? 1.5 : 2);
  }

  // Convert the authored feel knobs into the per-second rates _tick works in. Both are
  // authored in FRAMES so they keep meaning if the frame count changes; internally the
  // playhead is in progress units (0..1), hence dividing by the number of steps.
  _deriveFeel() {
    const steps = Math.max(this.count - 1, 1);
    this.minRate = this.settleFps / steps; // progress per second
    this.maxLag = this.maxLagFrames / steps; // progress
    // A speed ceiling below the settle floor would deadlock the two clamps against each
    // other (the floor would never be reachable), so the floor always wins that tie.
    this.maxRate = this.maxSpeedFps > 0
      ? Math.max(this.maxSpeedFps, this.settleFps) / steps
      : 0; // 0 = uncapped
  }

  // Retune the feel at runtime. Only reachable from the console via the ?fph-tune hook
  // below — the point is to dial tau/settle against the real footage without a rebuild,
  // since none of this is judgeable from the numbers alone.
  setFeel({ tau, settleFps, maxLagFrames, maxSpeedFps } = {}) {
    if (Number.isFinite(tau)) this.tau = tau;
    if (Number.isFinite(settleFps)) this.settleFps = settleFps;
    if (Number.isFinite(maxLagFrames)) this.maxLagFrames = maxLagFrames;
    if (Number.isFinite(maxSpeedFps)) this.maxSpeedFps = maxSpeedFps;
    this._deriveFeel();
    return {
      tau: this.tau, settleFps: this.settleFps,
      maxLagFrames: this.maxLagFrames, maxSpeedFps: this.maxSpeedFps,
      worstCaseCatchUpMs: this.maxRate ? Math.round((this.maxLag / this.maxRate) * 1000) : null
    };
  }

  init() {
    if (!this.ctx || !this.count || !this.base) return;

    // No pin / no scrub: stack everything and show poster + all text groups.
    if (reducedMotion()) {
      this.section.classList.add('is-static');
      this.groups.forEach((g) => g.classList.add('is-active'));
      return;
    }

    this.bgColor = this._readBg();
    this._sizeCanvas();
    window.addEventListener('resize', this._resize);

    // Place the copy before waiting on the sequence: the first group's entry is a rise from
    // inside the frame, so it has to already be sitting there when the section scrolls into
    // view, even on a connection where that beats frame 0. _setupScroll recomputes.
    this._computeGroupWindows();
    this._updateText(0);

    this._loadFrames().then(() => {
      this._sizeCanvas(); // frame 0 is decoded — re-cap the backing store to its resolution
      this._render(); // paint frame 0 under the (fading) poster
      this.section.classList.add('is-ready');
      this._setupScroll();
      ScrollTrigger.refresh();
    });
  }

  _frameUrl(i) {
    const n = String(i + 1).padStart(3, '0');
    return `${this.base}f_${n}.${this.ext}`;
  }

  // Download order: coarse to fine. Passes of halving stride (every 16th frame, then
  // every 8th, ...) so the decoded frames stay spread evenly over the whole descent at
  // every point during the download, instead of forming a loaded prefix and an empty
  // tail. _frameAt clamps to the nearest decoded frame, so with sequential loading a
  // visitor who scrolled early hit a frozen image; coarse-to-fine degrades to a
  // low-frame-rate scrub over the full descent instead. Frame 0 is first either way.
  _loadOrder() {
    const order = [];
    const seen = new Uint8Array(this.count);
    for (let stride = 16; stride >= 1; stride >>= 1) {
      for (let i = 0; i < this.count; i += stride) {
        if (!seen[i]) { seen[i] = 1; order.push(i); }
      }
    }
    return order;
  }

  // Load frames through a bounded worker pool instead of firing all N fetch+decode
  // calls at once. A storm of concurrent createImageBitmap decodes spikes memory and
  // saturates the connection pool — on iOS that alone can hang or kill the tab.
  // We resolve as soon as frame 0 is decoded so the descent goes interactive, then
  // stream the rest in the background in _loadOrder.
  _loadFrames() {
    const supportsBitmap = 'createImageBitmap' in window;
    const load = (i) =>
      fetch(this._frameUrl(i))
        .then((r) => r.blob())
        .then((b) => (supportsBitmap ? createImageBitmap(b) : this._blobToImage(b)))
        .then((img) => { this.frames[i] = img; })
        .catch(() => { /* leave a hole; _frameAt clamps to the nearest loaded frame */ });

    const concurrency = this.useMobileSet ? 3 : 6;
    const order = this._loadOrder().filter((i) => i !== 0); // 0 is awaited on its own
    return load(0).then(() => {
      let cursor = 0;
      const worker = () => (cursor >= order.length ? Promise.resolve() : load(order[cursor++]).then(worker));
      // Fire-and-forget the pool; callers only await frame 0.
      for (let k = 0; k < concurrency; k++) worker();
    });
  }

  _blobToImage(blob) {
    return new Promise((res, rej) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => { URL.revokeObjectURL(url); res(img); };
      img.onerror = () => { URL.revokeObjectURL(url); rej(); };
      img.src = url;
    });
  }

  // Size the backing store, capped at the SOURCE frame resolution. this.dpr is the
  // device cap; beyond the source's own pixels it buys no detail, only cost. _drawCover
  // fills the canvas HEIGHT from the frame's height, so that is the binding dimension.
  //
  // This matters more than it looks. Measured on a 1728x941 window at DPR 2: the buffer
  // was 3426x1882 = 6.4M px, holding 1920x1080 frames upscaled 1.8x — 3.2x the source's
  // pixels, re-uploaded to the GPU on every frame of the scrub. Capping to the source is
  // pixel-for-pixel identical output for 69% fewer pixels, and it is the largest per-frame
  // saving available here (the draw itself is already only ~0.02ms of main thread). Same
  // story on the 720^2 mobile set, where the 1.5 cap asked for 2.8x the source.
  //
  // Never below 1: a buffer under CSS size would be a real downscale, and the guard keeps
  // an odd viewport aspect from quietly softening the footage.
  _sizeCanvas() {
    const r = this.stage.getBoundingClientRect();
    const src = this.frames[0];
    const dpr = src && src.height
      ? Math.max(1, Math.min(this.dpr, src.height / Math.max(r.height, 1)))
      : this.dpr;
    this.canvas.width = Math.round(r.width * dpr);
    this.canvas.height = Math.round(r.height * dpr);
    this.canvas.style.width = r.width + 'px';
    this.canvas.style.height = r.height + 'px';
  }

  _readBg() {
    const fallback = { r: 2, g: 2, b: 38 };
    try {
      const m = getComputedStyle(this.section).backgroundColor.match(/\d+/g);
      if (m && m.length >= 3) return { r: +m[0], g: +m[1], b: +m[2] };
    } catch (e) { /* keep fallback */ }
    return fallback;
  }

  _bg(a) {
    const c = this.bgColor;
    return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;
  }

  // Nearest already-decoded frame to idx (defensive — by scrub time all are in).
  _frameAt(idx) {
    if (this.frames[idx]) return this.frames[idx];
    let lo = idx, hi = idx;
    while (lo >= 0 || hi < this.count) {
      if (lo >= 0 && this.frames[lo]) return this.frames[lo];
      if (hi < this.count && this.frames[hi]) return this.frames[hi];
      lo--; hi++;
    }
    return null;
  }

  // Draw a frame cover-cropped to the current target aspect (16:9 desktop / 1:1
  // mobile), sized to FILL the canvas height and centred horizontally. The source
  // is square, so we first crop it to the target aspect. Because height always
  // fills, any residual gap can only ever be on the sides (ultra-wide viewports),
  // where the caller feathers it into the background. Returns the drawn rect.
  _drawCover(img) {
    const ctx = this.ctx;
    const cw = this.canvas.width;
    const ch = this.canvas.height;
    const a = this.targetAspect;

    // Crop the source to the target aspect, biased by the focal point.
    const srcAspect = img.width / img.height;
    let sx, sy, sw, sh;
    if (srcAspect > a) { // source wider than target → trim its sides
      sh = img.height;
      sw = sh * a;
      sx = (img.width - sw) * this.focusX;
      sy = 0;
    } else { // source taller than target → trim its top/bottom
      sw = img.width;
      sh = sw / a;
      sx = 0;
      sy = (img.height - sh) * this.focusY;
    }

    // Fill the canvas height; width follows the target aspect, positioned by the
    // focal point. When the width overflows the canvas (the common case) focusX
    // chooses which slice shows; when it falls short (viewport wider than the
    // target aspect) dx ≥ 0 and the caller feathers the gap.
    const dh = ch;
    const dw = ch * a;
    const dx = (cw - dw) * this.focusX;
    const dy = 0;
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
    return { dx, dy, dw, dh };
  }

  // Fade the video's letterboxed edges into the background colour so the hard
  // frame edge isn't visible where the 1:1 video doesn't reach the viewport.
  _featherEdges({ dx, dy, dw, dh }) {
    const ctx = this.ctx;
    if (dx > 0.5) { // pillarbox → feather left & right
      const fw = Math.round(dw * 0.15);
      let g = ctx.createLinearGradient(dx, 0, dx + fw, 0);
      g.addColorStop(0, this._bg(1)); g.addColorStop(1, this._bg(0));
      ctx.fillStyle = g; ctx.fillRect(dx, dy, fw, dh);
      g = ctx.createLinearGradient(dx + dw - fw, 0, dx + dw, 0);
      g.addColorStop(0, this._bg(0)); g.addColorStop(1, this._bg(1));
      ctx.fillStyle = g; ctx.fillRect(dx + dw - fw, dy, fw, dh);
    }
    if (dy > 0.5) { // letterbox → feather top & bottom
      const fh = Math.round(dh * 0.15);
      let g = ctx.createLinearGradient(0, dy, 0, dy + fh);
      g.addColorStop(0, this._bg(1)); g.addColorStop(1, this._bg(0));
      ctx.fillStyle = g; ctx.fillRect(dx, dy, dw, fh);
      g = ctx.createLinearGradient(0, dy + dh - fh, 0, dy + dh);
      g.addColorStop(0, this._bg(0)); g.addColorStop(1, this._bg(1));
      ctx.fillStyle = g; ctx.fillRect(dx, dy + dh - fh, dw, fh);
    }
  }

  _render() {
    const ctx = this.ctx;
    if (!ctx) return;

    // Render a single crisp frame (nearest to the eased scrub position). Smoothness
    // comes from frame density plus damping the *scroll* (_tick), NOT from blending
    // frames: cross-dissolving adjacent frames showed a visible double image. Worth
    // knowing that that was judged on the old 100-frame set, where consecutive frames
    // were ~4x further apart in motion than they are now — a subtle blend may well
    // hold up on this set, if the scrub ever needs to go beyond per-frame steps.
    const idx = Math.round(Math.max(0, Math.min(this.frameState.i, this.count - 1)));
    const img = this._frameAt(idx);
    if (!img) return;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    const rect = this._drawCover(img);
    this._featherEdges(rect);
  }

  // Progress fraction at which the section finishes sliding into view and the pin takes
  // over — i.e. where the lead-in ends. Geometrically the scrub can start no earlier than
  // the section's top touching the viewport bottom, which is one viewport of scrolling,
  // hence the 1/screens ceiling. Depends on `screens`, so it is re-derived on refresh.
  _leadFraction() {
    return Math.min(this.leadFrac, 1 / this.screens);
  }

  // Pre-compute each text group's reveal window (as a fraction of the whole scrub).
  // Groups distribute evenly across the PINNED phase. A data-enter (0-100) on a group
  // overrides its entry point. The last group stays visible to the end.
  //
  // The FIRST group is an exception only in HOW it arrives, not when: it settles at the same
  // point every other group does (slot + fade), but it is already on screen while the section
  // is still sliding up, so it cannot sweep in from below the frame. It sits `travelIn` low
  // (data-rise) and rises into place from the very start of the scrub instead — a long, gentle
  // ramp rather than the ±fade band the others use. Its exit begins a fade band later, which
  // is also when the next group starts arriving, so the copy has settled before either.
  //
  // Entry is therefore stored as a band (enterFrom -> enterTo) rather than a point plus the
  // shared fade, which is the only thing that differs between the two cases.
  //
  // `travelIn` / `travelOut` are the per-group translate distances for the scroll-in and
  // scroll-out. They are measured from where the group actually RESTS in the stage, because
  // that differs per group now: the SCSS parks odd groups against the top edge and centres
  // even ones. Coming in, a group starts just below the stage's bottom edge
  // (stageH - top); leaving, it ends just above the top edge (top + its own height); plus
  // slack either way. A centred group works out to the stageH/2 + height/2 this used to
  // assume, so its feel is unchanged.
  //
  // Measured here rather than in _updateText so they are re-read on refresh/resize and never
  // per frame. offsetTop/offsetHeight, not getBoundingClientRect — the latter reports the
  // transformed box, and these elements are mid-transform whenever this runs. offsetTop is
  // relative to __groups, which is position:absolute inset:0 over the stage, so it is also
  // the offset from the stage's top edge.
  _computeGroupWindows() {
    const n = this.groups.length;
    // Start a little after the pin locks rather than on the exact frame it does, so a
    // group reads as a deliberate move rather than arriving with the lock.
    const start = Math.min(this._leadFraction() + 0.06, 0.6);
    const end = 0.98;
    const span = (end - start) / Math.max(n, 1);
    // Width of an EXIT band, straddling the boundary it hangs off (exit ± fade). Entries are
    // timed off their travel instead, and are the quicker of the two — see enterSpan below.
    this.fade = Math.min(span * 0.5, 0.14);
    const stageH = (this.stage && this.stage.offsetHeight) || window.innerHeight;
    // Scroll the whole scrub spans, in px: the denominator that turns ENTER_TRACK's px-per-px
    // into a fraction of progress.
    const scrubPx = Math.max(window.innerHeight * this.screens, 1);
    this.groupWindows = this.groups.map((el, i) => {
      const de = parseFloat(el.dataset.enter);
      const authored = Number.isFinite(de) ? clamp01(de / 100) : null;
      const slot = authored != null ? authored : start + i * span;
      // The lead-in rise, not a reveal from below. An authored data-enter opts out of it:
      // asking for a specific cue means asking for the reveal every other group gets.
      const rides = i === 0 && authored == null;
      const top = el.offsetTop; // resting offset from the stage's top edge
      const isLast = i === n - 1;
      const travelIn = rides ? stageH * this.riseFrac : stageH - top + 40;
      // Where the entry ends — the same point for every group, first one included. Only where
      // it BEGINS differs. Ending the first group's rise on its slot instead left it settled
      // for the width of a fade band before its exit began, and since a non-first group's
      // entry and exit meet with almost no gap (span - 2 x fade), that beat was the one
      // stretch of the whole scrub with nothing on screen answering the scroll but the
      // footage. Doubles as the point the drift below picks up from, so the arrival keeps the
      // shape it was tuned to and the drift starts from rest.
      const enterTo = slot + this.fade;
      // How much scroll the entry runs over, from the travel and the rate it should track the
      // finger at. Anchored at the settle point and measured BACKWARDS, so tuning the speed
      // moves the run-up rather than the hand-off. Clamped to at most a full exit band, which
      // is what keeps a group from starting to arrive before the one it replaces has started
      // to leave; and to at least half of one, so it can never become a jump cut.
      const enterSpan = Math.min(Math.max(travelIn / (ENTER_TRACK * scrubPx), this.fade * 0.5), this.fade * 2);
      return {
        el,
        enterFrom: rides ? 0 : enterTo - enterSpan,
        enterTo,
        // Each group scrolls out as the next scrolls in; the last (or only) one holds.
        exit: i < n - 1 ? slot + span : 1.2,
        travelIn,
        travelOut: top + el.offsetHeight + 40,
        // Whether the group is parked clear of the stage's bottom edge before it enters, and
        // so clipped either way — which is what lets _updateText keep it out of the a11y tree
        // until then. The first group's rise is short and never leaves the frame, so it has
        // to stay visible from the start.
        clippedBefore: travelIn >= stageH - top,
        // Only the group that holds to the end needs the drift — every other one is already
        // moving through its exit by then.
        drift: isLast ? stageH * this.driftFrac : 0,
        driftFrom: enterTo,
      };
    });
  }

  _setupScroll() {
    this._computeGroupWindows();
    this._updateText(0);

    // TWO triggers, because the descent starts scrubbing BEFORE it is fully in view and
    // GSAP cannot pin later than a trigger starts. Pinning at the earlier point would
    // freeze the section half-way up the viewport; scrubbing only from the pin point
    // leaves it sitting on a dead frame while it slides in. So: one trigger pins, the
    // other reports progress over a range that begins `leadPx` earlier.
    //
    // Both END at the same scroll position, so the frame mapping still spans exactly
    // `screens` viewports and the frame density we tuned is unchanged — the lead-in is
    // carved OUT of the pinned length, not added to it.
    const total = () => window.innerHeight * this.screens;
    const leadPx = () => total() * this._leadFraction();

    // Pin only. refreshPriority puts it ahead of the progress trigger below, so its pin
    // spacing is in place before that one measures across it.
    this.pinST = ScrollTrigger.create({
      trigger: this.section,
      start: 'top top',
      end: () => '+=' + Math.max(total() - leadPx(), 1),
      pin: true,
      invalidateOnRefresh: true,
      refreshPriority: 1,
    });

    // Progress only. Starts while the section is still rising into view: `start` is the
    // scroller position its top must reach, and that distance to the top of the viewport
    // is exactly the lead-in. `end` is a relative distance, so pin spacing can't skew it.
    // The playhead easing happens in _tick — deliberately not GSAP's `scrub`.
    this.st = ScrollTrigger.create({
      trigger: this.section,
      start: () => 'top ' + leadPx() + 'px',
      end: () => '+=' + total(),
      invalidateOnRefresh: true,
      onRefresh: (self) => { this._computeGroupWindows(); this.targetP = self.progress; this._wake(); },
      onUpdate: (self) => { this.targetP = self.progress; this._wake(); },
    });

    document.addEventListener('visibilitychange', this._onVisibility);
  }

  _wake() {
    if (this.raf == null) {
      this.lastTime = 0;
      this.raf = requestAnimationFrame(this._tick);
    }
  }

  _tick(now) {
    const dt = this.lastTime ? Math.min((now - this.lastTime) / 1000, 0.05) : 1 / 60;
    this.lastTime = now;

    // Exponential approach toward the scroll target, with a floor on the speed so the
    // tail can't crawl (see minRate). Monotonic — never crosses the target — so the
    // playhead always settles in the scrolled direction.
    let delta = this.targetP - this.currentP;

    // Give up on the surplus beyond maxLag rather than coasting through it. Lag is
    // velocity x tau and therefore unbounded, so a fast flick would otherwise leave the
    // playhead tens of frames behind and spend a second catching up after the scroll
    // already stopped. Discarding it only ever happens while scrolling faster than the
    // display can show distinct frames anyway, so nothing visible is skipped.
    if (Math.abs(delta) > this.maxLag) {
      this.currentP = this.targetP - (delta > 0 ? this.maxLag : -this.maxLag);
      delta = this.targetP - this.currentP;
    }

    let step = delta * (1 - Math.exp(-dt / this.tau));
    const minStep = this.minRate * dt;
    if (Math.abs(step) < minStep) step = delta < 0 ? -minStep : minStep;

    // Ceiling on the advance rate, so a fast flick plays the descent instead of skipping
    // through it. Applied after the floor because a speed cap can never legitimately be
    // slower than the settle floor (_deriveFeel already guarantees maxRate >= minRate).
    if (this.maxRate) {
      const maxStep = this.maxRate * dt;
      if (Math.abs(step) > maxStep) step = delta < 0 ? -maxStep : maxStep;
    }
    // Land exactly rather than overshoot. This is also what lets the loop below stop on
    // an equality test instead of an epsilon: the floor guarantees we get here.
    if (Math.abs(step) >= Math.abs(delta)) this.currentP = this.targetP;
    else this.currentP += step;

    const p = clamp01(this.currentP);
    this.frameState.i = p * (this.count - 1);
    // Re-blit only when the rounded frame index actually changes. During the settle
    // tail (and any sub-frame drift) this skips redundant identical canvas draws,
    // keeping the main thread free so scrolling elsewhere on the page stays fluid.
    const idx = Math.round(Math.max(0, Math.min(this.frameState.i, this.count - 1)));
    if (idx !== this._lastDrawnIdx) { this._render(); this._lastDrawnIdx = idx; }
    // Text runs off the raw SCROLL position, not the eased playhead. It used to share the
    // playhead so the copy carried the same momentum as the footage, but the maxSpeed
    // ceiling made that coupling harmful: the playhead can sit 60 frames behind, so copy
    // keyed to it arrived long after the scroll had passed its cue (measured: first group
    // still at 0 opacity at progress 0.5, well after the pin). Scroll position also matches
    // what the reveals actually mean — "show this once we're THIS far down the section".
    this._updateText(clamp01(this.targetP));

    if (this.currentP === this.targetP) this.raf = null;
    else this.raf = requestAnimationFrame(this._tick);
  }

  // Drive the intro fade-out and the text-group reveals from the raw scroll progress (see
  // the note in _tick for why this is deliberately NOT the eased playhead).
  _updateText(p) {
    if (this.intro) {
      const a = 1 - smoothstep(0.03, 0.15, p);
      this.intro.style.opacity = a;
      this.intro.style.transform = `translateY(${(1 - a) * -40}px)`;
      this.intro.style.visibility = a < 0.01 ? 'hidden' : 'visible';
    }
    // Groups scroll THROUGH the frame rather than fading: in from below, out past the top.
    // Around the boundary between two groups the outgoing one starts leaving a fade band
    // before the incoming one starts arriving, and both finish together — so they pass each
    // other in the frame instead of handing over across an empty beat, and the arrival is
    // the quicker half of the exchange. The first group is the one that doesn't arrive from
    // below: it rises a short way into place instead (see _computeGroupWindows).
    // Opacity is binary and only flips while the group is fully translated clear of the
    // stage (which is overflow:hidden, so it is clipped either way); it exists to keep
    // parked copy out of the a11y tree, not as part of the reveal — hence clippedBefore,
    // since a group whose entry stays inside the frame has nothing to hide behind.
    // On top of that, the group that holds to the end keeps drifting slowly upwards for the
    // rest of the scrub (see `drift` in _computeGroupWindows). LINEAR, unlike everything else
    // here: constant travel per pixel scrolled is what reads as "the scroll is working", where
    // an eased ramp would decelerate to a stop exactly where the footage is quietest and
    // re-create the problem. The velocity step at the hand-off doesn't show — the entry's tail
    // is near rest and the drift is an order of magnitude slower than it.
    for (const w of this.groupWindows) {
      const inP = smoothstep(w.enterFrom, w.enterTo, p);
      const outP = smoothstep(w.exit - this.fade, w.exit + this.fade, p);
      const driftP = w.drift ? clamp01((p - w.driftFrom) / Math.max(1 - w.driftFrom, 0.001)) : 0;
      const onScreen = (inP > 0.001 || !w.clippedBefore) && outP < 0.999;
      w.el.style.opacity = onScreen ? 1 : 0;
      w.el.style.transform =
        `translateY(${(1 - inP) * w.travelIn - outP * w.travelOut - driftP * w.drift}px)`;
      w.el.style.visibility = onScreen ? 'visible' : 'hidden';
    }
  }

  _onVisibility() {
    if (document.hidden) {
      if (this.raf != null) { cancelAnimationFrame(this.raf); this.raf = null; }
    } else {
      // Snap to target so we don't coast through the whole descent on return.
      this.currentP = this.targetP;
      const p = clamp01(this.currentP);
      this.frameState.i = p * (this.count - 1);
      this._render();
      this._updateText(clamp01(this.targetP));
    }
  }

  _resize() {
    // iOS/Android fire 'resize' when the address bar shows/hides: same width, a modest
    // height delta. Re-pinning (ScrollTrigger.refresh) on every such event is what makes
    // the surrounding page fight scroll on touch, so ignore address-bar-only resizes and
    // react only to a width change or a large height change (orientation / real relayout).
    const w = window.innerWidth, h = window.innerHeight;
    const addressBarOnly = w === this._lastW && Math.abs(h - this._lastH) < this._lastH * 0.25;
    this._lastW = w; this._lastH = h;
    if (addressBarOnly) return;

    // Never swap the loaded frame set / pin length at runtime (that would discard
    // this.frames[] and re-stream) — the set is frozen to the device chosen at init.
    // targetAspect is frozen with it (the frames are pre-cropped to that aspect), so
    // only dpr (crispness) adapts here.
    this.dpr = Math.min(window.devicePixelRatio || 1, this.useMobileSet ? 1.5 : 2);
    // Refresh FIRST: when the descent is pinned, ScrollTrigger holds the section
    // at position:fixed with an explicit size that doesn't track the viewport
    // until a refresh. Measuring the stage before that reads the stale pinned
    // size and leaves the canvas mis-sized (e.g. after a mobile orientation
    // change). Refresh, then measure the settled geometry.
    ScrollTrigger.refresh();
    this._sizeCanvas();
    this._render();
  }
}

// Load ?fph-tune to dial the scrub feel from the console against the real footage:
//
//   const h = document.querySelector('[data-fph]').fph
//   h.setFeel({ tau: 0.45 })        // more float (lag = scroll speed x tau)
//   h.setFeel({ settleFps: 20 })    // longer, steppier coast after you stop
//   h.setFeel({ maxSpeedFps: 30 })  // hard flicks play at half rate instead of skipping
//   h.setFeel({ maxLagFrames: 12 }) // tighter catch-up (and makes maxSpeed moot)
//   h.setFeel({ maxSpeedFps: 0 })   // speed ceiling off
//
// setFeel returns the resolved knobs plus worstCaseCatchUpMs (= maxLag / maxSpeed), which
// is the number to watch when trading those two off. Whatever settles, write it back as
// data-smooth / data-settle / data-max-speed / data-max-lag on the section in
// block-front-page-hero.twig. Gated on the query param so production carries no debug
// surface.
const tuning = /[?&]fph-tune\b/.test(window.location.search);

const sections = document.querySelectorAll('section.front-page-hero[data-fph]');
if (sections.length) {
  sections.forEach((s) => {
    const hero = new FrontPageHero(s);
    if (tuning) s.fph = hero;
    hero.init();
  });
  // Re-measure once late-loading assets settle so pin/start/end are correct.
  window.addEventListener('load', () => ScrollTrigger.refresh());
}

// Scroll-down affordance on the surface band: smooth-scroll to the descent below.
const scrollDown = document.querySelector('[data-fph-scroll]');
if (scrollDown) {
  scrollDown.addEventListener('click', () => {
    const descent = document.querySelector('.front-page-hero[data-fph]');
    const top = descent ? descent.getBoundingClientRect().top + window.scrollY : window.innerHeight;
    window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
  });

  // The affordance has done its job the moment you start scrolling, so it leaves in the
  // direction it was pointing: lifts by its own height and fades out over the first
  // quarter-viewport of scroll. Scrubbed rather than a one-shot, so scrolling back to the
  // top brings it back. Notes:
  //  - this writes to the WRAPPER, and the intro reveal below writes to the BUTTON inside it,
  //    so the two never fight over the same properties on the same element;
  //  - autoAlpha (not opacity) so it also gets visibility:hidden at 0, keeping a fully
  //    faded button out of the a11y tree and un-clickable while it's parked;
  //  - y and end are functions so both re-measure on ScrollTrigger.refresh() (which
  //    invalidateOnRefresh forces): the button is bigger at the top breakpoint, and the lift
  //    is defined as its own height, so crossing that breakpoint has to re-read it.
  const scrollWrap = scrollDown.closest('.front-page-hero__scroll');
  const band = scrollDown.closest('.front-page-hero');
  if (scrollWrap && band && !reducedMotion()) {
    gsap.to(scrollWrap, {
      // Its own height, so it clearly departs rather than drifts.
      y: () => -(scrollDown.offsetHeight || 56),
      autoAlpha: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: band,
        start: 'top top',
        end: () => `+=${window.innerHeight * 0.25}`,
        scrub: true,
        invalidateOnRefresh: true,
      },
    });
  }
}

// Intro reveal on the surface band. On load, not on a trigger — this is the top of the page,
// it is already in view.
//
// The order is deliberately NOT DOM order: the paragraph is a kicker sitting ABOVE the
// heading, but the heading leads and the kicker follows it in, so the eye lands on the
// headline first. Then the scroll affordance, then the symbol in the heading blinks — the
// last one being the only beat that isn't an entrance, so it wants everything else settled
// before it fires.
//
// gsap.matchMedia builds this only where motion is welcome and reverts it, inline styles and
// all, if the preference changes mid-session. The SCSS holds the same elements at opacity 0
// under the same conditions, so nothing flashes before this footer bundle runs — and that is
// why these are fromTo and not from, which would read the CSS 0 as the value to animate TO.
const INTRO_RISE = 24;   // px each beat travels up into place
const INTRO_BEAT = 0.3;  // seconds between one beat starting and the next
const INTRO_BLINK = 0.18; // seconds per half-blink, so a full close-and-open is twice this
const INTRO_BLINK_GAP = 0.2; // seconds the eyes stay open between the two blinks

const introBand = document.querySelector('.front-page-hero--surface:not(.is-static)');
if (introBand) {
  const intro = introBand.querySelector('[data-fph-intro]');
  const heading = intro && intro.querySelector('h1');
  // Every paragraph as ONE beat, so a two-line kicker can't arrive in pieces.
  const kicker = intro ? Array.from(intro.querySelectorAll('p')) : [];
  const button = introBand.querySelector('.fph-scroll');
  // Authored inside the WYSIWYG, so treat it as optional — no span, no blink, no crash.
  const symbol = heading ? heading.querySelector('span') : null;
  const beats = [heading, kicker.length ? kicker : null, button].filter(Boolean);

  if (beats.length) {
    gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
      const tl = gsap.timeline({ defaults: { ease: 'power2.out', duration: 0.6 } });
      beats.forEach((target, i) => {
        tl.fromTo(target, { opacity: 0, y: INTRO_RISE }, { opacity: 1, y: 0 }, i * INTRO_BEAT);
      });
      // Two DISTINCT blinks rather than one four-phase pulse: a tween per blink (repeat 1 +
      // yoyo = one close and open), held apart by INTRO_BLINK_GAP. Without the hold the pair
      // runs continuously and reads as a single slow flicker. The repeat count is odd on
      // purpose — that is what lands each blink back at full opacity, so an interrupted tail
      // can never leave the symbol switched off. Fresh vars per tween rather than one shared
      // object, since GSAP holds on to the one it is given.
      if (symbol) {
        const blink = () => ({ opacity: 0, duration: INTRO_BLINK, repeat: 1, yoyo: true, ease: 'none' });
        tl.to(symbol, blink(), '+=0.2') // let the button land first
          .to(symbol, blink(), `+=${INTRO_BLINK_GAP}`);
      }
    });
  }
}